import React, { FC, MouseEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import { NavLinkProps } from 'react-router-dom';
import { WithAnalytics } from 'ia-react-core/~/interfaces/WithAnalytics';

import { SCP } from 'ia-react-core/~/interfaces/SCP';
import Router, { isLegacy } from 'ia-react-core/~/services/router/Router';
import Badge, { BadgeProps } from '../../notification/Badge';
import { StyledLink, StyledLinkText, StyledLoader, StyledLoaderWrapper } from './SideNavLink.styles';

export interface SideNavLinkProps extends NavLinkProps, Omit<BadgeProps, keyof SCP<'div'>>, WithAnalytics {
  /** Link content */
  children?: ReactNode;
  /** Defines the content of a badge to show to the right of the link. */
  badgeContent?: ReactNode;
  /** External link. */
  external?: boolean;
  /** Disables the link. */
  disabled?: boolean;
  /** Matches the path exactly. If false, will match url that starts with the path. */
  exact?: boolean;
  /** Handle for the click event. */
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void | boolean | Promise<void | boolean>;
}

interface SideNavLinkStatics {
  StyledLink: typeof StyledLink;
}

const SideNavLink: FC<SideNavLinkProps> & SideNavLinkStatics = ({
  to, badgeContent, badgeType, unreadText, maxCount, children, disabled = false, exact, onClick, ...props
}) => {
  const history = Router.useHistory();
  const [busy, setBusy] = useState(false);

  const handleClick = useCallback(async (event: MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      event.preventDefault();
    } else {
      const result = onClick?.(event);
      if (result !== true && typeof result !== 'undefined') {
        event.preventDefault();
        if (typeof result === 'object') {
          setBusy(true);
          try {
            const promiseResult = await result;
            if (promiseResult !== false) {
              history.push(to as Location);
            }
          } catch {
            // ignore
          } finally {
            setBusy(false);
          }
        }
      }
    }
  }, [disabled, onClick, history, to]);

  const linkProps = useMemo(() => {
    if (!isLegacy) {
      return { end: exact };
    }
    return { exact };
  }, [exact]);

  return (
    <StyledLink
      tabIndex={0}
      data-testid="SideNavLink"
      {...props}
      {...linkProps}
      to={to}
      disabled={disabled}
      aria-disabled={String(disabled)}
      onClick={handleClick}
    >
      <StyledLinkText>{children}</StyledLinkText>
      {busy && (
        <StyledLoaderWrapper>
          <StyledLoader size={20} />
        </StyledLoaderWrapper>
      )}
      {badgeContent && (
        <Badge
          badgeType={badgeType}
          unreadText={unreadText}
          maxCount={maxCount}
        >
          {badgeContent}
        </Badge>
      )}
    </StyledLink>
  );
};

SideNavLink.StyledLink = StyledLink;

export default SideNavLink;
